import { CacheEntry, CacheConfig } from './types';
import { debug, info } from './logger';

/**
 * LRU缓存实现
 * 支持基于访问频率、最近访问时间和生存时间的缓存策略
 */
export class LRUCache<K, V> {
  private cache: Map<K, CacheEntry<V>>;
  private config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.cache = new Map<K, CacheEntry<V>>();
    this.config = {
      maxSize: config.maxSize || 500,
      ttl: config.ttl || 24 * 60 * 60 * 1000 // 默认24小时
    };
  }

  /**
   * 获取缓存项
   */
  get(key: K): V | undefined {
    const entry = this.cache.get(key);

    if (!entry) {
      return undefined;
    }

    // 检查是否过期
    if (this.isExpired(entry)) {
      this.cache.delete(key);
      debug(`缓存项已过期: ${String(key)}`);
      return undefined;
    }

    // 更新访问统计
    entry.accessCount += 1;
    entry.lastAccessed = Date.now();

    debug(`命中缓存: ${String(key)}`);
    return entry.value;
  }

  /**
   * 设置缓存项
   */
  set(key: K, value: V): void {
    // 如果达到最大容量，先清理
    if (this.cache.size >= this.config.maxSize) {
      this.evict();
    }

    const entry: CacheEntry<V> = {
      value,
      timestamp: Date.now(),
      accessCount: 0,
      lastAccessed: Date.now()
    };

    this.cache.set(key, entry);
    debug(`添加缓存: ${String(key)}`);
  }

  /**
   * 删除缓存项
   */
  delete(key: K): boolean {
    return this.cache.delete(key);
  }

  /**
   * 清空缓存
   */
  clear(): void {
    this.cache.clear();
    info('缓存已清空');
  }

  /**
   * 获取缓存大小
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * 检查缓存项是否存在
   */
  has(key: K): boolean {
    if (!this.cache.has(key)) {
      return false;
    }

    const entry = this.cache.get(key)!;
    if (this.isExpired(entry)) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * 检查缓存项是否过期
   */
  private isExpired(entry: CacheEntry<V>): boolean {
    const now = Date.now();
    return now - entry.timestamp > this.config.ttl;
  }

  /**
   * 驱逐策略：结合访问频率和最近访问时间
   * 计算得分 = 访问次数 * 0.4 + 最近访问时间的新鲜度 * 0.6
   * 驱逐得分最低的项
   */
  private evict(): void {
    if (this.cache.size === 0) return;

    const now = Date.now();
    let lowestScore = Number.MAX_VALUE;
    let keyToEvict: K | null = null;

    // 找出得分最低的项
    this.cache.forEach((entry, key) => {
      const recency = (now - entry.lastAccessed) / this.config.ttl; // 0到1之间，越小越新鲜
      const recencyScore = 1 - recency; // 转换为新鲜度得分，越高越好

      // 计算总得分，访问次数和新鲜度各占一定权重
      const score = (entry.accessCount * 0.4) + (recencyScore * 0.6);

      if (score < lowestScore) {
        lowestScore = score;
        keyToEvict = key;
      }
    });

    // 驱逐得分最低的项
    if (keyToEvict !== null) {
      this.cache.delete(keyToEvict);
      debug(`缓存驱逐: ${String(keyToEvict)}`);
    }
  }
}

// 创建单例实例
export const wordInfoCache = new LRUCache<string, any>({
  maxSize: 500,
  ttl: 24 * 60 * 60 * 1000 // 24小时
});

// 创建会话缓存，用于存储临时数据
export const sessionCache = new LRUCache<string, any>({
  maxSize: 100,
  ttl: 30 * 60 * 1000 // 30分钟
});

/**
 * 从SessionStorage加载数据
 */
export const loadFromSessionStorage = (key: string): any => {
  try {
    const data = sessionStorage.getItem(key);
    if (!data) return null;

    return JSON.parse(data);
  } catch (error) {
    debug(`从SessionStorage加载失败: ${key}`, error);
    sessionStorage.removeItem(key);
    return null;
  }
};

/**
 * 保存数据到SessionStorage
 */
export const saveToSessionStorage = (key: string, data: any): void => {
  try {
    const serialized = JSON.stringify(data);
    sessionStorage.setItem(key, serialized);
    debug(`保存到SessionStorage: ${key}`);
  } catch (error) {
    debug(`保存到SessionStorage失败: ${key}`, error);
  }
};